import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Account from './pages/Account';
import TopicLandingPage from './pages/TopicLandingPage';
import QuizSetup from './pages/QuizSetup';
import QuizSession from './pages/QuizSession';
import QuizStandard from './pages/QuizStandard';
import QuizSkillBuilder from './pages/QuizSkillBuilder';
import Results from './pages/Results';
import Progress from './pages/Progress';
import Games from './pages/Games';
import GameSetup from './pages/GameSetup';
import Flashcards from './pages/Flashcards';
import Matching from './pages/Matching';
import WordSearch from './pages/WordSearch';
import AdminDashboard from './pages/AdminDashboard';
import AdminQuestions from './pages/AdminQuestions';
import AdminGames from './pages/AdminGames';
import AdminCaseStudies from './pages/AdminCaseStudies';
import AdminReferenceSheets from './pages/AdminReferenceSheets';
import AdminStudyGuides from './pages/AdminStudyGuides';
import AdminAbout from './pages/AdminAbout';
import AdminContact from './pages/AdminContact';
import AdminContactSubmissions from './pages/AdminContactSubmissions';
import AdminTiers from './pages/AdminTiers';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminLogoSettings from './pages/AdminLogoSettings';
import AdminHeroSettings from './pages/AdminHeroSettings';
import AdminQuizExampleImage from './pages/AdminQuizExampleImage';
import CaseStudies from './pages/CaseStudies';
import CaseStudyIntro from './pages/CaseStudyIntro';
import CaseStudySession from './pages/CaseStudySession';
import CaseStudyResults from './pages/CaseStudyResults';
import ReferenceSheets from './pages/ReferenceSheets';
import ReferenceSheetViewer from './pages/ReferenceSheetViewer';
import About from './pages/About';
import StudyGuides from './pages/StudyGuides';
import Contact from './pages/Contact';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import BillingSuccess from './pages/BillingSuccess';
import ProtectedRoute from './components/ProtectedRoute';
import AuthOnlyRoute from './components/AuthOnlyRoute';
import { Toaster } from './components/ui/sonner';
import './App.css';

function AppRouter() {
  const location = useLocation();
  
  // Handle Supabase auth callback (both hash and path)
  if (location.hash?.includes('access_token=') || location.hash?.includes('error=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/topics" element={<TopicLandingPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/products" element={<StudyGuides />} />
        <Route path="/billing/success" element={
          <AuthOnlyRoute>
            <BillingSuccess />
          </AuthOnlyRoute>
        } />
        <Route path="/subscription/success" element={
          <AuthOnlyRoute>
            <SubscriptionSuccess />
          </AuthOnlyRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        } />
        {/* Reference Sheets - requires auth */}
        <Route path="/reference-sheets" element={
          <ProtectedRoute>
            <ReferenceSheets />
          </ProtectedRoute>
        } />
        <Route path="/reference-sheets/:sheetId" element={
          <ProtectedRoute>
            <ReferenceSheetViewer />
          </ProtectedRoute>
        } />
        {/* New unified quiz flow */}
        <Route path="/quiz/setup" element={
          <ProtectedRoute>
            <QuizSetup />
          </ProtectedRoute>
        } />
        <Route path="/quiz/setup/:topicKey" element={
          <ProtectedRoute>
            <QuizSetup />
          </ProtectedRoute>
        } />
        <Route path="/quiz/session" element={
          <ProtectedRoute>
            <QuizSession />
          </ProtectedRoute>
        } />
        {/* Legacy routes - redirect to new flow */}
        <Route path="/quiz/standard/:topicId" element={
          <ProtectedRoute>
            <QuizSetup />
          </ProtectedRoute>
        } />
        <Route path="/quiz/skill-builder" element={
          <ProtectedRoute>
            <QuizSkillBuilder />
          </ProtectedRoute>
        } />
        <Route path="/results" element={
          <ProtectedRoute>
            <Results />
          </ProtectedRoute>
        } />
        <Route path="/progress" element={
          <ProtectedRoute>
            <Progress />
          </ProtectedRoute>
        } />
        {/* Games Section */}
        <Route path="/games" element={
          <ProtectedRoute>
            <Games />
          </ProtectedRoute>
        } />
        <Route path="/games/:gameType/setup" element={
          <ProtectedRoute>
            <GameSetup />
          </ProtectedRoute>
        } />
        <Route path="/games/flashcards/play" element={
          <ProtectedRoute>
            <Flashcards />
          </ProtectedRoute>
        } />
        <Route path="/games/matching/play" element={
          <ProtectedRoute>
            <Matching />
          </ProtectedRoute>
        } />
        <Route path="/games/wordsearch/play" element={
          <ProtectedRoute>
            <WordSearch />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/questions" element={
          <ProtectedRoute adminOnly>
            <AdminQuestions />
          </ProtectedRoute>
        } />
        <Route path="/admin/games" element={
          <ProtectedRoute adminOnly>
            <AdminGames />
          </ProtectedRoute>
        } />
        <Route path="/admin/case-studies" element={
          <ProtectedRoute adminOnly>
            <AdminCaseStudies />
          </ProtectedRoute>
        } />
        <Route path="/admin/reference-sheets" element={
          <ProtectedRoute adminOnly>
            <AdminReferenceSheets />
          </ProtectedRoute>
        } />
        <Route path="/admin/study-guides" element={
          <ProtectedRoute adminOnly>
            <AdminStudyGuides />
          </ProtectedRoute>
        } />
        <Route path="/admin/pages/about" element={
          <ProtectedRoute adminOnly>
            <AdminAbout />
          </ProtectedRoute>
        } />
        <Route path="/admin/pages/contact" element={
          <ProtectedRoute adminOnly>
            <AdminContact />
          </ProtectedRoute>
        } />
        <Route path="/admin/contact-submissions" element={
          <ProtectedRoute adminOnly>
            <AdminContactSubmissions />
          </ProtectedRoute>
        } />
        <Route path="/admin/tiers" element={
          <ProtectedRoute adminOnly>
            <AdminTiers />
          </ProtectedRoute>
        } />
        <Route path="/admin/subscriptions" element={
          <ProtectedRoute adminOnly>
            <AdminSubscriptions />
          </ProtectedRoute>
        } />
        <Route path="/admin/logo-settings" element={
          <ProtectedRoute adminOnly>
            <AdminLogoSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/hero-settings" element={
          <ProtectedRoute adminOnly>
            <AdminHeroSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/quiz-example-image" element={
          <ProtectedRoute adminOnly>
            <AdminQuizExampleImage />
          </ProtectedRoute>
        } />
        {/* Case Studies Section */}
        <Route path="/case-studies" element={
          <ProtectedRoute>
            <CaseStudies />
          </ProtectedRoute>
        } />
        <Route path="/case-studies/:caseId" element={
          <ProtectedRoute>
            <CaseStudyIntro />
          </ProtectedRoute>
        } />
        <Route path="/case-studies/:caseId/session" element={
          <ProtectedRoute>
            <CaseStudySession />
          </ProtectedRoute>
        } />
        <Route path="/case-studies/:caseId/results/:attemptId" element={
          <ProtectedRoute>
            <CaseStudyResults />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;